import { isJust, toMaybe } from "../lib/maybe";
import type { ZwPrintSize } from "../lib/zwConfig";
import { PRINT_SIZES } from "../lib/zwConfig";

export type PrintPageSize = {
  id: ZwPrintSize;
  label: string;
  width: string;
  height: string;
};

const DIMENSIONS: Record<ZwPrintSize, { width: string; height: string }> = {
  a1: { width: "594mm", height: "841mm" },
  a2: { width: "420mm", height: "594mm" },
  a3: { width: "297mm", height: "420mm" },
  a4: { width: "210mm", height: "297mm" },
  a5: { width: "148mm", height: "210mm" },
  letter: { width: "8.5in", height: "11in" },
  legal: { width: "8.5in", height: "14in" },
  tabloid: { width: "11in", height: "17in" },
  trade: { width: "6in", height: "9in" },
  digest: { width: "5.5in", height: "8.5in" },
};

export const EXPORT_PRINT_SIZES: PrintPageSize[] = PRINT_SIZES.map((size) => ({
  id: size.id,
  label: size.label,
  ...DIMENSIONS[size.id],
}));

export function printPageSizeFor(id: ZwPrintSize): PrintPageSize {
  const byId = toMaybe(EXPORT_PRINT_SIZES.find((size) => size.id === id));
  if (isJust(byId)) return byId.value;
  const fallback = toMaybe(EXPORT_PRINT_SIZES.find((size) => size.id === "a5"));
  if (isJust(fallback)) return fallback.value;
  return EXPORT_PRINT_SIZES[0] as PrintPageSize;
}

/**
 * Convert a CSS length to Word twips (1/20 pt).
 * Supports `in`/`mm`/`cm`/`px`/`pt`/`em`/`rem`. Em/rem use `emTwips`
 * (default 240 = 12pt body).
 */
export function cssLengthToTwip(
  value: string,
  options?: { emTwips?: number },
): number {
  const match = value.trim().match(/^([\d.]+)\s*(in|mm|cm|px|pt|em|rem)?$/i);
  if (match === null) return 12240;
  const n = Number.parseFloat(match[1]!);
  const unit = (match[2] ?? "in").toLowerCase();
  const emTwips = options?.emTwips ?? 240;
  switch (unit) {
    case "in":
      return Math.round(n * 1440);
    case "mm":
      return Math.round((n * 1440) / 25.4);
    case "cm":
      return Math.round((n * 1440) / 2.54);
    case "px":
      return Math.round((n * 1440) / 96);
    case "pt":
      return Math.round(n * 20);
    case "em":
    case "rem":
      return Math.round(n * emTwips);
    default:
      return Math.round(n * 1440);
  }
}

/** Convert a CSS length (`6in`, `148mm`, …) to CSS pixels at 96dpi. */
export function cssLengthToPx(value: string): number {
  const match = value.trim().match(/^([\d.]+)\s*(in|mm|cm|px)$/i);
  if (match === null) return 600;
  const n = Number.parseFloat(match[1]!);
  switch (match[2]!.toLowerCase()) {
    case "in":
      return n * 96;
    case "mm":
      return n * (96 / 25.4);
    case "cm":
      return n * (96 / 2.54);
    case "px":
      return n;
    default:
      return n;
  }
}

export function printPageSizePixels(size: PrintPageSize): {
  width: number;
  height: number;
} {
  return {
    width: Math.ceil(cssLengthToPx(size.width)),
    height: Math.ceil(cssLengthToPx(size.height)),
  };
}
